/**
 * Roster configuration — defines the complete station/unit/seat structure
 * for all 5 battalions and SOD (Special Operations Division).
 *
 * DCVFD has 4 stations: 510, 513, 518, 520
 * Each station has an Engine, Ambulance, and Utility at minimum.
 * Station 510 also has Rescue 510, Station 513 also has Tower 513.
 * Each battalion has a Command section (BC buggy + Safety).
 * SOD (Special Operations Division) is a separate section.
 */

// ── Seat Definitions ──────────────────────────────────────────────────

export interface SeatDef {
  id: string;
  label: string;
  /** Minimum staffing seat — must be filled for unit to be in service */
  required: boolean;
  /** Which member types/ranks can fill this seat */
  eligibleRanks: string[];
}

/** Engine seats (6 total, 3 minimum) */
export const ENGINE_SEATS: SeatDef[] = [
  {
    id: 'io',
    label: 'Incident Officer (IO)',
    required: true,
    eligibleRanks: ['captain', 'lieutenant', 'sergeant', 'technician'],
  },
  {
    id: 'dpo',
    label: 'Driver/Pump Operator (DPO)',
    required: true,
    eligibleRanks: ['captain', 'lieutenant', 'sergeant', 'technician', 'firefighter'],
  },
  {
    id: 'lead_ff',
    label: 'Lead Firefighter',
    required: true,
    eligibleRanks: ['captain', 'lieutenant', 'sergeant', 'technician', 'firefighter'],
  },
  { id: 'ff_1', label: 'Firefighter', required: false, eligibleRanks: ['firefighter', 'recruit'] },
  { id: 'ff_2', label: 'Firefighter', required: false, eligibleRanks: ['firefighter', 'recruit'] },
  { id: 'ff_3', label: 'Firefighter', required: false, eligibleRanks: ['firefighter', 'recruit'] },
];

/** Ambulance seats (4 total, 2 minimum) */
export const AMBULANCE_SEATS: SeatDef[] = [
  {
    id: 'lead_tech',
    label: 'Lead Tech / Crew Leader',
    required: true,
    eligibleRanks: ['captain', 'lieutenant', 'sergeant', 'crew_leader', 'lead_tech'],
  },
  {
    id: 'driver',
    label: 'Driver',
    required: true,
    eligibleRanks: ['captain', 'lieutenant', 'sergeant', 'technician', 'firefighter', 'ems_provider'],
  },
  {
    id: 'att_1',
    label: 'Attendant',
    required: false,
    eligibleRanks: ['ems_recruit', 'intern', 'ride_along', 'firefighter', 'ems_provider'],
  },
  {
    id: 'att_2',
    label: 'Attendant',
    required: false,
    eligibleRanks: ['ems_recruit', 'intern', 'ride_along', 'firefighter', 'ems_provider'],
  },
];

/** Tower 513 seats (6 total, 3 minimum) */
export const TOWER_SEATS: SeatDef[] = [
  {
    id: 'ao',
    label: 'Aerial Operator',
    required: true,
    eligibleRanks: ['captain', 'lieutenant', 'sergeant', 'technician'],
  },
  {
    id: 'io',
    label: 'Incident Officer (IO)',
    required: true,
    eligibleRanks: ['captain', 'lieutenant', 'sergeant', 'technician'],
  },
  {
    id: 'lead_ff',
    label: 'Lead Firefighter',
    required: true,
    eligibleRanks: ['captain', 'lieutenant', 'sergeant', 'technician', 'firefighter'],
  },
  { id: 'ff_1', label: 'Firefighter', required: false, eligibleRanks: ['firefighter', 'recruit'] },
  { id: 'ff_2', label: 'Firefighter', required: false, eligibleRanks: ['firefighter', 'recruit'] },
  { id: 'ff_3', label: 'Firefighter', required: false, eligibleRanks: ['firefighter', 'recruit'] },
];

/** Rescue 510 seats (6 total, 3 minimum) */
export const RESCUE_SEATS: SeatDef[] = [
  {
    id: 'io',
    label: 'Incident Officer (IO)',
    required: true,
    eligibleRanks: ['captain', 'lieutenant', 'sergeant', 'technician'],
  },
  {
    id: 'do',
    label: 'Driver/Operator',
    required: true,
    eligibleRanks: ['captain', 'lieutenant', 'sergeant', 'technician', 'firefighter'],
  },
  {
    id: 'lead_ff',
    label: 'Lead Firefighter',
    required: true,
    eligibleRanks: ['captain', 'lieutenant', 'sergeant', 'technician', 'firefighter'],
  },
  { id: 'ff_1', label: 'Firefighter', required: false, eligibleRanks: ['firefighter', 'recruit'] },
  { id: 'ff_2', label: 'Firefighter', required: false, eligibleRanks: ['firefighter', 'recruit'] },
  { id: 'ff_3', label: 'Firefighter', required: false, eligibleRanks: ['firefighter', 'recruit'] },
];

/** Brush 518 seats (2 total, 1 minimum — F350 pickup with pump sled) */
export const BRUSH_SEATS: SeatDef[] = [
  {
    id: 'lead_ff',
    label: 'Lead Firefighter (Driver)',
    required: true,
    eligibleRanks: ['captain', 'lieutenant', 'sergeant', 'technician', 'firefighter'],
  },
  { id: 'ff_1', label: 'Firefighter', required: false, eligibleRanks: ['firefighter', 'recruit', 'technician'] },
];

/** Utility seats (2 total, 1 minimum) */
export const UTILITY_SEATS: SeatDef[] = [
  {
    id: 'operator',
    label: 'Operator',
    required: true,
    eligibleRanks: ['captain', 'lieutenant', 'sergeant', 'technician', 'firefighter'],
  },
  { id: 'rider', label: 'Rider', required: false, eligibleRanks: ['firefighter', 'recruit', 'technician'] },
];

/** Battalion Chief buggy seats (2 total) */
export const BC_BUGGY_SEATS: SeatDef[] = [
  {
    id: 'bc',
    label: 'Battalion Chief',
    required: true,
    eligibleRanks: ['battalion_chief', 'deputy_chief', 'assistant_chief'],
  },
  {
    id: 'fist',
    label: 'FIST (Support)',
    required: false,
    eligibleRanks: ['captain', 'lieutenant', 'sergeant', 'technician', 'firefighter'],
  },
];

/** Safety officer seat */
export const SAFETY_SEATS: SeatDef[] = [
  {
    id: 'safety',
    label: 'Safety Officer',
    required: true,
    eligibleRanks: ['captain', 'lieutenant', 'sergeant', 'technician'],
  },
];

// ── Unit Definitions ──────────────────────────────────────────────────

export interface UnitDef {
  id: string;
  label: string;
  type: 'engine' | 'ambulance' | 'medic' | 'tower' | 'rescue' | 'utility' | 'brush' | 'buggy' | 'safety';
  seats: SeatDef[];
}

// ── Station Definitions ───────────────────────────────────────────────

export interface StationDef {
  id: string;
  label: string;
  units: UnitDef[];
}

export const STATION_510: StationDef = {
  id: '510',
  label: 'Station 510',
  units: [
    { id: 'E510', label: 'Engine 510', type: 'engine', seats: ENGINE_SEATS },
    { id: 'A510', label: 'Ambulance 510', type: 'ambulance', seats: AMBULANCE_SEATS },
    { id: 'M510', label: 'Medic 510', type: 'medic', seats: AMBULANCE_SEATS },
    { id: 'R510', label: 'Rescue 510', type: 'rescue', seats: RESCUE_SEATS },
    { id: 'U510', label: 'Utility 510', type: 'utility', seats: UTILITY_SEATS },
  ],
};

export const STATION_513: StationDef = {
  id: '513',
  label: 'Station 513',
  units: [
    { id: 'E513', label: 'Engine 513', type: 'engine', seats: ENGINE_SEATS },
    { id: 'A513', label: 'Ambulance 513', type: 'ambulance', seats: AMBULANCE_SEATS },
    { id: 'M513', label: 'Medic 513', type: 'medic', seats: AMBULANCE_SEATS },
    { id: 'TW513', label: 'Tower 513', type: 'tower', seats: TOWER_SEATS },
    { id: 'U513', label: 'Utility 513', type: 'utility', seats: UTILITY_SEATS },
  ],
};

export const STATION_518: StationDef = {
  id: '518',
  label: 'Station 518',
  units: [
    { id: 'E518', label: 'Engine 518', type: 'engine', seats: ENGINE_SEATS },
    { id: 'A518', label: 'Ambulance 518', type: 'ambulance', seats: AMBULANCE_SEATS },
    { id: 'M518', label: 'Medic 518', type: 'medic', seats: AMBULANCE_SEATS },
    { id: 'B518', label: 'Brush 518', type: 'brush', seats: BRUSH_SEATS },
    { id: 'U518', label: 'Utility 518', type: 'utility', seats: UTILITY_SEATS },
  ],
};

export const STATION_520: StationDef = {
  id: '520',
  label: 'Station 520',
  units: [
    { id: 'E520', label: 'Engine 520', type: 'engine', seats: ENGINE_SEATS },
    { id: 'A520', label: 'Ambulance 520', type: 'ambulance', seats: AMBULANCE_SEATS },
    { id: 'M520', label: 'Medic 520', type: 'medic', seats: AMBULANCE_SEATS },
    { id: 'U520', label: 'Utility 520', type: 'utility', seats: UTILITY_SEATS },
  ],
};

export const ALL_STATIONS: StationDef[] = [STATION_510, STATION_513, STATION_518, STATION_520];

// ── Battalion Structure ───────────────────────────────────────────────

export interface BattalionDef {
  id: number;
  label: string;
  stations: StationDef[];
  /** Battalion-level command units (not tied to a station) */
  commandUnits: UnitDef[];
}

function makeBattalion(num: number): BattalionDef {
  return {
    id: num,
    label: `Battalion ${num}`,
    stations: ALL_STATIONS,
    commandUnits: [
      { id: `BC${num}`, label: `Buggy ${num}`, type: 'buggy', seats: BC_BUGGY_SEATS },
      { id: `SAF${num}`, label: `Safety ${num}`, type: 'safety', seats: SAFETY_SEATS },
    ],
  };
}

export const BATTALIONS: BattalionDef[] = [
  makeBattalion(1),
  makeBattalion(2),
  makeBattalion(3),
  makeBattalion(4),
  makeBattalion(5),
];

// ── SOD (Special Operations Division) ─────────────────────────────────

export interface SODDef {
  id: string;
  label: string;
  /** SOD has flexible assignment slots */
  maxSlots: number;
}

export const SOD: SODDef = {
  id: 'sod',
  label: 'Special Operations Division (SOD)',
  maxSlots: 30,
};

// ── Fire Chief (approval authority) ───────────────────────────────────

export const FIRE_CHIEF_EMAIL = 'chool@dcvfd.org';
export const FIRE_CHIEF_NAME = 'Chris Hool';

// ── Helpers ───────────────────────────────────────────────────────────

/** Get all unique unit IDs across all battalions */
export function getAllUnitIds(): string[] {
  const ids: string[] = [];
  for (const bn of BATTALIONS) {
    for (const station of bn.stations) {
      for (const unit of station.units) {
        if (!ids.includes(unit.id)) ids.push(unit.id);
      }
    }
    for (const unit of bn.commandUnits) {
      ids.push(unit.id);
    }
  }
  return ids;
}

/** Get total seat count for a unit */
export function getUnitSeatCount(unit: UnitDef): number {
  return unit.seats.length;
}

/** Get minimum staffing count for a unit */
export function getUnitMinStaffing(unit: UnitDef): number {
  return unit.seats.filter((s) => s.required).length;
}
